import React, { useState, useEffect } from 'react'

export const FloatingToolbar = ({ position, onCommand, visible, activeFormats, onInsertLink }) => {
    // Local state for font size input to prevent excessive re-renders/lag while typing
    const [localFontSize, setLocalFontSize] = useState(16);

    useEffect(() => {
        // Sync local state with active format from selection
        const size = parseInt(activeFormats.fontSize);
        if (!isNaN(size)) {
            setLocalFontSize(size);
        }
    }, [activeFormats.fontSize]);

    const handleFontSizeCommit = (value) => {
        let val = parseInt(value);
        if (isNaN(val) || val < 1) val = 16;
        onCommand('fontSize', val);
        setLocalFontSize(val); // Ensure local state reflects committed value
    };

    if (!visible) return null;

    const getButtonStyle = (isActive) => ({
        background: isActive ? '#148DFD' : 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '4px 8px',
        color: isActive ? '#ffffff' : '#374151',
        fontWeight: 'bold',
        borderRadius: '4px',
        transition: 'all 0.15s ease'
    });

    const getLinkButtonStyle = () => ({
        background: activeFormats.link ? '#148DFD' : 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '4px 8px',
        color: activeFormats.link ? '#ffffff' : '#148DFD',
        fontWeight: 'bold',
        borderRadius: '4px',
        transition: 'all 0.15s ease',
        textDecoration: 'underline'
    });

    return (
        <div
            style={{
                position: 'absolute',
                top: position.top,
                left: position.left,
                transform: 'translate(-50%, -100%)',
                backgroundColor: 'white',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                borderRadius: '8px',
                padding: '4px',
                zIndex: 1000,
                display: 'flex',
                gap: '4px',
                marginTop: '-10px',
                border: '1px solid #e5e7eb'
            }}
            className="floating-toolbar-container"
            onMouseDown={(e) => {
                // Prevent focus loss for buttons, but allow it for input
                if (e.target.tagName !== 'INPUT') {
                    e.preventDefault();
                }
            }}
        >
            <button style={getButtonStyle(activeFormats.bold)} onClick={() => onCommand('bold')} title="Bold">B</button>
            <button style={{ ...getButtonStyle(activeFormats.italic), fontStyle: 'italic' }} onClick={() => onCommand('italic')} title="Italic">I</button>
            <button style={{ ...getButtonStyle(activeFormats.underline), textDecoration: 'underline' }} onClick={() => onCommand('underline')} title="Underline">U</button>
            <div style={{ width: '1px', background: '#ccc', margin: '0 4px' }}></div>
            <button style={getLinkButtonStyle()} onClick={onInsertLink} title="Insert Link">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
            </button>
            <div style={{ width: '1px', background: '#ccc', margin: '0 4px' }}></div>
            <button style={getButtonStyle(activeFormats.unorderedList)} onClick={() => onCommand('insertUnorderedList')} title="Bullet List">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="8" y1="6" x2="21" y2="6" />
                    <line x1="8" y1="12" x2="21" y2="12" />
                    <line x1="8" y1="18" x2="21" y2="18" />
                    <circle cx="3" cy="6" r="1.5" fill="currentColor" />
                    <circle cx="3" cy="12" r="1.5" fill="currentColor" />
                    <circle cx="3" cy="18" r="1.5" fill="currentColor" />
                </svg>
            </button>
            <div style={{ width: '1px', background: '#ccc', margin: '0 4px' }}></div>

            {/* Font Size Control */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                <button
                    style={getButtonStyle(false)}
                    onClick={() => handleFontSizeCommit(localFontSize - 1)}
                    title="Decrease Font Size"
                >
                    -
                </button>
                <input
                    type="number"
                    value={localFontSize}
                    onChange={(e) => setLocalFontSize(e.target.value)}
                    onBlur={() => handleFontSizeCommit(localFontSize)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.target.blur(); // Triggers onBlur to commit
                        }
                    }}
                    style={{
                        width: '36px',
                        textAlign: 'center',
                        fontSize: '13px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '4px',
                        padding: '2px',
                        color: '#374151',
                        fontWeight: 600,
                        outline: 'none',
                        '-moz-appearance': 'textfield' // Remove spin buttons in Firefox
                    }}
                    className="no-spin" // Add corresponding CSS if global styles allow, or rely on internal style
                />
                <style>
                    {`
                        /* Hide spin buttons for WebKit browsers */
                        input.no-spin::-webkit-outer-spin-button,
                        input.no-spin::-webkit-inner-spin-button {
                            -webkit-appearance: none;
                            margin: 0;
                        }
                    `}
                </style>
                <button
                    style={getButtonStyle(false)}
                    onClick={() => handleFontSizeCommit(parseInt(localFontSize) + 1)}
                    title="Increase Font Size"
                >
                    +
                </button>
            </div>
            <div style={{ width: '1px', background: '#ccc', margin: '0 4px' }}></div>
            <button style={getButtonStyle(activeFormats.justifyLeft)} onClick={() => onCommand('justifyLeft')} title="Align Left">L</button>
            <button style={getButtonStyle(activeFormats.justifyCenter)} onClick={() => onCommand('justifyCenter')} title="Align Center">C</button>
            <button style={getButtonStyle(activeFormats.justifyRight)} onClick={() => onCommand('justifyRight')} title="Align Right">R</button>
        </div>
    );
};
